import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { useField } from 'formik';
import { AutoSizer, MultiGrid } from 'react-virtualized';

import { validateAllocationPercentage } from '../context/AllocationFormContext';
import { useProjectActivitiesContext } from '../context/PositionActivitiesContext';
import { useAllocationForm } from '../hooks/useAllocationForm';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import DiamondIcon from 'Assets/icons/diamond.svg';
import TriangleDownIcon from 'Assets/icons/triangle-down-filled.svg';

const CATEGORY_TYPE = 'category';
const SUBCATEGORY_TYPE = 'subcategory';
const ACTIVITY_TYPE = 'activity';

const ROW_HEIGHT = 30;
const SCROLLBAR_WIDTH = 20;
const MONTHS_COLUMN_WIDTH = 128;
const FIXED_COLUMN_WIDTH = 400;
const HEADER_COLUMN_COUNT = 4;
const HEADER_ROW_COUNT = 2;

const CATEGORY_HEADER_KEY = 'category_header';
const OWNER_HEADER_KEY = 'owner_header';
const SOURCE_HEADER_KEY = 'source_header';
const COST_HEADER_KEY = 'cost_header';
const HEADERS_MAP = [
    { key: CATEGORY_HEADER_KEY, label: $t('Category'), collapsable: false },
    { key: OWNER_HEADER_KEY, label: $t('Owner'), collapsable: true },
    { key: SOURCE_HEADER_KEY, label: $t('Source'), collapsable: true },
    { key: COST_HEADER_KEY, label: $t('Cost'), collapsable: true },
];

const DEFAULT_COLLAPSED_HEADERS = {
    [OWNER_HEADER_KEY]: false,
    [SOURCE_HEADER_KEY]: false,
    [COST_HEADER_KEY]: false,
};

const GanttChart = () => {
    const [collapsedHeaderCells, setCollapsedHeaderCells] = useState(DEFAULT_COLLAPSED_HEADERS);

    const { ganttChart, expandedRows, toggleRow } = useProjectActivitiesContext();

    const containerRef = useRef();
    const wrapperRef = useRef();
    const multiGridRef = useRef();

    const getFlatRows = useCallback(
        (items, level = 0) =>
            items.flatMap(({ data, items, columns }) => {
                const row = { expandable: !!items?.length, data: { ...data, level }, columns };
                if (!expandedRows[data.key] || !items?.length) {
                    return row;
                }

                return [row, ...getFlatRows(items, level + 1)];
            }),
        [expandedRows],
    );

    const columnKeys = useMemo(() => {
        if (!ganttChart.data) {
            return [];
        }

        return ganttChart.data.columns_keys;
    }, [ganttChart.data]);

    const headers = useMemo(() => {
        if (!ganttChart.data) {
            return [];
        }

        return ganttChart.data.headers;
    }, [ganttChart.data]);

    const rows = useMemo(() => {
        if (!ganttChart.data) {
            return [];
        }

        return getFlatRows(ganttChart.data.rows);
    }, [ganttChart.data, getFlatRows]);

    // Recompute the virtualized grid whenever the visible row count changes (expanding/collapsing
    // rows) or a column header is toggled. MultiGrid caches cell sizes and positions internally,
    // so without an explicit recompute newly revealed rows (such as activity leaf rows under an
    // expanded sub-category) are never rendered. (IB-748)
    useEffect(() => {
        multiGridRef.current?.recomputeGridSize();
    }, [collapsedHeaderCells, rows.length]);

    const dataColumnCount = useMemo(() => {
        if (!ganttChart.data) {
            return 0;
        }

        return ganttChart.data.columns_count;
    }, [ganttChart.data]);

    const dataRowCount = useMemo(() => rows.length, [rows]);

    const columnCount = useMemo(() => HEADER_COLUMN_COUNT + dataColumnCount, [dataColumnCount]);
    const rowCount = useMemo(() => HEADER_ROW_COUNT + dataRowCount, [dataRowCount]);

    const lineColumnIndex = useMemo(() => {
        if (!columnKeys.length || !ganttChart.data?.line) {
            return;
        }

        const index = columnKeys.findIndex((key, index) => !!ganttChart.data.line && ganttChart.data.line[key]);

        return index ? index + HEADER_COLUMN_COUNT : -1;
    }, [columnKeys, ganttChart.data]);

    const getPeriodWidth = useCallback(() => {
        const width = dataColumnCount ? (containerRef.current?.clientWidth - FIXED_COLUMN_WIDTH - 2) / dataColumnCount : 0;
        return Math.max(width, MONTHS_COLUMN_WIDTH);
    }, [dataColumnCount]);

    const getRowHeight = useCallback(
        ({ index }) => {
            if (index - HEADER_ROW_COUNT >= dataRowCount) {
                return SCROLLBAR_WIDTH;
            }

            return ROW_HEIGHT;
        },
        [dataRowCount],
    );

    const getColumnWidth = useCallback(
        ({ index }) => {
            if (index === 0) {
                return FIXED_COLUMN_WIDTH;
            }

            if (index === 1) {
                return !collapsedHeaderCells[OWNER_HEADER_KEY] ? 128 : 20;
            }

            if (index === 2) {
                return !collapsedHeaderCells[SOURCE_HEADER_KEY] ? 128 : 20;
            }

            if (index === 3) {
                return !collapsedHeaderCells[COST_HEADER_KEY] ? 128 : 20;
            }

            return getPeriodWidth();
        },
        [collapsedHeaderCells, getPeriodWidth],
    );

    const cellClassNames = useCallback(
        (columnIndex, rowIndex) => {
            const lastRowIndex = rowCount - 1;
            const lastColumnIndex = columnCount - 1;

            return classNames({
                // Borders
                'tw-border-t': rowIndex > 0, // No top border for header row
                'tw-border-b': rowIndex < lastRowIndex, // No bottom border for last row
                'tw-border-r': columnIndex < lastColumnIndex, // No right border for last column
                'tw-border-l': columnIndex > 0, // No left border for first column
                // Border-less
                'tw-border-b-0': rowIndex === 0 && columnIndex < HEADER_COLUMN_COUNT, // No bottom border for first header row first columns
                'tw-border-t-0': rowIndex === 1 && columnIndex < HEADER_COLUMN_COUNT, // No bottom border for first header row first columns
                'tw-border-r-0': rowIndex === 0 && columnIndex > HEADER_COLUMN_COUNT - 1 && !headers[rowIndex].columns[columnIndex - HEADER_COLUMN_COUNT + 1], // No right border for header cells with no next header
                'tw-border-l-0': rowIndex === 0 && columnIndex > HEADER_COLUMN_COUNT - 1 && !headers[rowIndex].columns[columnIndex - HEADER_COLUMN_COUNT], // No left border for header cells with no header
                // Backgrounds
                'tw-bg-background-1': rowIndex === 0 && columnIndex > HEADER_COLUMN_COUNT - 1, // First header row
                'tw-bg-background-2': rowIndex === 1 && columnIndex > HEADER_COLUMN_COUNT - 1, // Second header row
            });
        },
        [rowCount, columnCount, headers],
    );

    const cellManager = useCallback(
        ({ columnIndex, rowIndex }) => {
            if (rowIndex < HEADER_ROW_COUNT) {
                if (columnIndex < HEADER_COLUMN_COUNT) {
                    if (rowIndex !== HEADER_ROW_COUNT - 1) {
                        // Only render header cells on the last header row
                        return null;
                    }

                    const header = HEADERS_MAP[columnIndex];
                    return (
                        <HeaderCell
                            heading={header.label}
                            collapsed={collapsedHeaderCells[header.key]}
                            onToggleCollapse={() => setCollapsedHeaderCells((prev) => ({ ...prev, [header.key]: !prev[header.key] }))}
                            collapsable={header.collapsable}
                        />
                    );
                }

                const header = headers[rowIndex];
                const heading = header?.columns ? header.columns[columnIndex - HEADER_COLUMN_COUNT] : null;
                const monthKey = columnKeys[columnIndex - HEADER_COLUMN_COUNT];

                return rowIndex === 0 ? <HeaderYearCell year={heading} /> : <HeaderMonthCell month={heading} monthKey={monthKey} />;
            }

            const row = rows[rowIndex - HEADER_ROW_COUNT];
            const rowData = row ? row.data : null;

            if (columnIndex < HEADER_COLUMN_COUNT) {
                if (columnIndex === 0) {
                    if (rowData.type === CATEGORY_TYPE) {
                        return (
                            <CategoryDataCell
                                value={rowData.description}
                                expandable={rowData.expandable}
                                expanded={expandedRows[rowData.key]}
                                onToggleExpansionClick={() => toggleRow(rowData.key)}
                            />
                        );
                    }

                    if (rowData.type === SUBCATEGORY_TYPE) {
                        return (
                            <SubcategoryDataCell
                                value={rowData.description}
                                expanded={expandedRows[rowData.key]}
                                onToggleExpansionClick={() => toggleRow(rowData.key)}
                            />
                        );
                    }

                    if (rowData.type === ACTIVITY_TYPE) {
                        return <ActivityDataCell value={rowData.description} />;
                    }
                }

                if (columnIndex === 1) {
                    return !collapsedHeaderCells[OWNER_HEADER_KEY] ? <OwnerDataCell value={rowData.owner_name} /> : null;
                }

                if (columnIndex === 2) {
                    return !collapsedHeaderCells[SOURCE_HEADER_KEY] ? <SourceDataCell value={rowData.source_name} /> : null;
                }

                if (columnIndex === 3) {
                    return !collapsedHeaderCells[COST_HEADER_KEY] && rowData.type === ACTIVITY_TYPE ? <CostDataCell activityId={rowData.id} /> : null;
                }
            }

            const columnKey = columnKeys[columnIndex - HEADER_COLUMN_COUNT];
            const cellData = row ? row.columns[columnKey] : null;
            const lineData = ganttChart.data.line && ganttChart.data.line[columnKey] ? ganttChart.data.line[columnKey] : null;
            const startLineData = ganttChart.data.start_line && ganttChart.data.start_line[columnKey] ? ganttChart.data.start_line[columnKey] : null;
            const endLineData = ganttChart.data.end_line && ganttChart.data.end_line[columnKey] ? ganttChart.data.end_line[columnKey] : null;
            return (
                <DataCell
                    monthKey={columnKey}
                    cellData={cellData}
                    rowData={rowData}
                    isFirstRow={rowIndex === HEADER_ROW_COUNT}
                    isLastRow={rowIndex === rowCount - 1}
                    isLastColumn={columnIndex === columnCount - 1}
                    lineData={lineData}
                    startLineData={startLineData}
                    endLineData={endLineData}
                    getPeriodWidth={getPeriodWidth}
                />
            );
        },
        [headers, rows, expandedRows, toggleRow, getPeriodWidth, columnKeys, ganttChart.data, columnCount, collapsedHeaderCells],
    );

    const cellRenderer = useCallback(
        ({ columnIndex, rowIndex, key, style }) => {
            // Empty cell for scrollbar row
            if (rowIndex - HEADER_ROW_COUNT >= dataRowCount) {
                return <div key={key} style={style} className="tw-bg-grey"></div>;
            }

            const classnames = classNames(cellClassNames(columnIndex, rowIndex), 'tw-flex tw-items-center tw-text-sm');

            return (
                <div key={key} style={style} className={classnames}>
                    {cellManager({ columnIndex, rowIndex })}
                </div>
            );
        },
        [cellClassNames, cellManager],
    );

    return (
        <div ref={containerRef} className="tw-min-h-0 tw-h-full tw-w-full tw-mb-10">
            <div
                ref={wrapperRef}
                className="tw-border-grey tw-border-2 tw-rounded-lg tw-overflow-hidden"
                style={{ height: rowCount * ROW_HEIGHT + 4 + SCROLLBAR_WIDTH, maxHeight: containerRef.current?.clientHeight || '100%' }} // +4 for up and bottom borders
            >
                <AutoSizer>
                    {({ width, height }) => (
                        <MultiGrid
                            ref={multiGridRef}
                            width={width - 2}
                            height={height - 4}
                            rowHeight={getRowHeight}
                            rowCount={rowCount + 1} // +1 for scrollbar row
                            fixedRowCount={HEADER_ROW_COUNT}
                            columnCount={columnCount}
                            fixedColumnCount={HEADER_COLUMN_COUNT}
                            columnWidth={getColumnWidth}
                            cellRenderer={cellRenderer}
                            scrollToColumn={lineColumnIndex}
                            enableFixedColumnScroll
                        />
                    )}
                </AutoSizer>
            </div>
        </div>
    );
};

const HeaderCell = ({ heading, collapsable, collapsed, onToggleCollapse }) => {
    return (
        <div className="tw-w-full tw-h-full tw-px-1.5 tw-text-sm tw-font-semibold">
            {!collapsable ? (
                <div className="tw-h-full tw-flex tw-items-center">
                    <span className="tw-text-center tw-w-full">{heading}</span>
                </div>
            ) : (
                <div className="tw-h-full tw-flex tw-items-center tw-justify-between">
                    {!collapsed && <div className="tw-text-center tw-w-full">{heading}</div>}
                    <button onClick={onToggleCollapse}>
                        {!collapsed ? (
                            <ChevronLeftIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                        ) : (
                            <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

const HeaderYearCell = ({ year }) => {
    return (
        <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-px-1.5 tw-text-sm tw-font-semibold">
            <span>{year}</span>
        </div>
    );
};

const HeaderMonthCell = ({ month, monthKey }) => {
    const { getTotalAllocationForMonth } = useAllocationForm();
    const { isResourceAllocationMode, allocations } = useProjectActivitiesContext();

    const allocatedPercentage = useMemo(() => getTotalAllocationForMonth(monthKey), [getTotalAllocationForMonth, monthKey]);

    return (
        <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-px-1.5 tw-text-sm">
            <div className="tw-w-full tw-flex tw-items-end tw-justify-between">
                <div className="tw-font-semibold">{month}</div>
                {Number.isFinite(allocatedPercentage) && (
                    <div
                        className={classNames('tw-text-xs tw-font-medium', {
                            'tw-text-charcoal-75': !isResourceAllocationMode,
                            'tw-text-blue-75': allocatedPercentage <= 100 && allocatedPercentage >= 0 && isResourceAllocationMode,
                            'tw-text-red-75': (allocatedPercentage > 100 || allocatedPercentage < 0) && isResourceAllocationMode,
                        })}
                    >
                        {allocations.loading ? (
                            <div className="tw-mb-1">
                                <LoadingSpinner size="xxs" />
                            </div>
                        ) : (
                            <span>{allocatedPercentage}%</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const CategoryDataCell = ({ value, expanded, onToggleExpansionClick }) => {
    return (
        <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-gap-x-1.5 tw-px-1.5 tw-bg-background-3">
            <div className="tw-min-w-0 tw-flex tw-items-center tw-gap-x-1.5">
                <div className="tw-w-3 tw-flex tw-items-center tw-shrink-0">
                    <button onClick={() => onToggleExpansionClick()}>
                        {expanded ? (
                            <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                        ) : (
                            <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                        )}
                    </button>
                </div>
                <div className="tw-truncate">{value}</div>
            </div>
        </div>
    );
};

const SubcategoryDataCell = ({ value, expanded, onToggleExpansionClick }) => {
    return (
        <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-gap-x-1.5 tw-px-1.5 tw-bg-background" style={{ paddingLeft: 24 }}>
            <div className="tw-min-w-0 tw-flex tw-items-center tw-gap-x-1.5">
                <div className="tw-w-3 tw-flex tw-items-center tw-shrink-0">
                    <button onClick={() => onToggleExpansionClick()}>
                        {expanded ? (
                            <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                        ) : (
                            <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                        )}
                    </button>
                </div>
                <div className="tw-truncate">{value}</div>
            </div>
        </div>
    );
};

const ActivityDataCell = ({ value }) => {
    return (
        <div className="tw-bg-white tw-px-1.5 tw-truncate" style={{ paddingLeft: 42 }}>
            {value}
        </div>
    );
};

const OwnerDataCell = ({ value }) => {
    return (
        <div className="tw-w-full tw-flex tw-items-center tw-px-1.5">
            <div className="tw-truncate">{value}</div>
        </div>
    );
};

const SourceDataCell = ({ value }) => {
    return (
        <div className="tw-w-full tw-flex tw-items-center tw-px-1.5">
            <div className="tw-truncate">{value}</div>
        </div>
    );
};

const CostDataCell = ({ activityId }) => {
    const { getActivityCostById } = useProjectActivitiesContext();

    const cost = useMemo(() => getActivityCostById(activityId), [getActivityCostById, activityId]);

    return <div className="tw-w-full tw-flex tw-items-center tw-justify-end tw-px-1.5 inbud-numeric-text">{formatToFixedMoneyWithLocale(cost)}</div>;
};

const DataCell = ({ monthKey, cellData, rowData, isFirstRow, isLastRow, isLastColumn, lineData, startLineData, endLineData, getPeriodWidth }) => {
    const { isResourceAllocationMode, getAllocatedPercentageByMonthAndActivityId, allocations } = useProjectActivitiesContext();

    const allocatedPercentage = useMemo(
        () => getAllocatedPercentageByMonthAndActivityId(monthKey, rowData.id),
        [getAllocatedPercentageByMonthAndActivityId, monthKey, rowData.id],
    );

    if (cellData?.allocability && rowData.type === ACTIVITY_TYPE && isResourceAllocationMode) {
        return (
            <div className="tw-w-full tw-h-full">
                <AllocationInput id={rowData.id} monthKey={monthKey} allocability={cellData.allocability} />
            </div>
        );
    }

    return (
        <Fragment>
            {allocatedPercentage !== undefined && cellData?.allocability && (
                <div className="tw-absolute tw-inset-0 tw-z-30 tw-flex tw-items-center tw-justify-center tw-text-2xs tw-font-semibold tw-text-charcoal">
                    {allocations.loading ? <LoadingSpinner size="xxs" /> : <span>{allocatedPercentage}%</span>}
                </div>
            )}
            {cellData?.bar && (
                <Bar
                    offsetPercentage={cellData.bar.offset_percentage}
                    lengthPercentage={cellData.bar.length_percentage}
                    isClosedStart={cellData.bar.is_closed_start}
                    isClosedEnd={cellData.bar.is_closed_end}
                    percentage={cellData.bar.percentage}
                    status={cellData.bar.status}
                    periodWidth={getPeriodWidth()}
                    isKeyMilestone={cellData.bar.type === 'key-milestone'}
                />
            )}
            {lineData && <VerticalLine offsetPercentage={lineData.offset} isFirstRow={isFirstRow} isLastRow={isLastRow} isLastColumn={isLastColumn} />}
            {startLineData && (
                <VerticalLine
                    offsetPercentage={startLineData.offset}
                    isFirstRow={isFirstRow}
                    isLastRow={isLastRow}
                    isLastColumn={isLastColumn}
                    label={isFirstRow ? startLineData.label : null}
                    color="blue"
                />
            )}
            {endLineData && (
                <VerticalLine
                    offsetPercentage={endLineData.offset}
                    isFirstRow={isFirstRow}
                    isLastRow={isLastRow}
                    isLastColumn={isLastColumn}
                    label={isFirstRow ? endLineData.label : null}
                    color="blue"
                />
            )}
            {cellData?.completion_line && (
                <CompletionLine
                    offsetPercentage={cellData.completion_line.offset_percentage}
                    lengthPercentage={cellData.completion_line.length_percentage}
                    isClosedEnd={cellData.completion_line.is_closed_end}
                    periodWidth={getPeriodWidth()}
                />
            )}
        </Fragment>
    );
};

const Bar = ({ offsetPercentage, lengthPercentage, percentage, status, isClosedStart, isClosedEnd, periodWidth, isKeyMilestone }) => {
    periodWidth -= isClosedEnd ? 2 : 0; // Reduce border width (2px) if has closed end

    let left = (periodWidth * offsetPercentage) / 100;
    let width = (periodWidth * lengthPercentage) / 100;

    return (
        <div className="tw-absolute tw-z-20 tw-top-0 tw-bottom-0 tw-my-auto tw-text-2xs" style={{ left, width, height: 18 }}>
            {isClosedEnd && isKeyMilestone && (
                <DiamondIcon className="tw-absolute tw-w-3 tw-h-3 tw-z-10 -tw-right-[5px] tw-top-0 tw-bottom-0 tw-my-auto tw-fill-yellow-300" />
            )}
            <div
                className={classNames('tw-absolute tw-inset-0 tw-overflow-hidden tw-border-t tw-border-b tw-border-[#223322]', {
                    'tw-bg-blue-75 tw-text-white': status === 'completed',
                    'tw-bg-background-4': status === 'overdue',
                    'tw-bg-background-3': status === 'in_progress',
                    'tw-border-l tw-rounded-l': isClosedStart,
                    'tw-border-r tw-rounded-r': isClosedEnd,
                })}
            >
                {percentage > 0 && ['in_progress', 'overdue'].includes(status) && (
                    <div
                        className={classNames('tw-absolute tw-inset-0', {
                            'tw-bg-background-6': status === 'overdue',
                            'tw-bg-background-5': status === 'in_progress',
                        })}
                        style={{ width: `${percentage}%` }}
                    />
                )}
            </div>
        </div>
    );
};

const VerticalLine = ({ offsetPercentage, isFirstRow, isLastRow, isLastColumn, label = null, color = null }) => {
    const labelOffset = useMemo(() => (offsetPercentage > 60 && isLastColumn ? offsetPercentage - 9 : offsetPercentage), [offsetPercentage, isLastColumn]);
    const height = useMemo(() => (isLastRow ? '100%' : 'calc(100% + 2px)'), []);
    return (
        <Fragment>
            <div
                className={classNames('tw-w-0.5 tw-flex tw-justify-center tw-absolute tw-z-20 tw-top-0 tw-bottom-0', {
                    'tw-bg-charcoal-50': !color,
                    'tw-bg-blue': color === 'blue',
                })}
                style={{ left: `${offsetPercentage}%`, height }}
            >
                {isFirstRow && (
                    <TriangleDownIcon className={classNames('tw-min-w-3 tw-w-3', { 'tw-text-charcoal-50': !color, 'tw-text-blue': color === 'blue' })} />
                )}
            </div>
            {label && (
                <div className="tw-absolute tw-top-3 tw-inline-block tw-text-2xs tw-font-medium" style={{ left: `${labelOffset}%`, zIndex: 21 }}>
                    <div className="tw-text-nowrap" style={{ writingMode: 'vertical-lr', textOrientation: 'mixed' }}>
                        {label}
                    </div>
                </div>
            )}
        </Fragment>
    );
};

const CompletionLine = ({ offsetPercentage, lengthPercentage, isClosedEnd, periodWidth }) => {
    periodWidth -= isClosedEnd ? 2 : 0; // Reduce border width (2px) if has closed end

    let left = (periodWidth * offsetPercentage) / 100;
    let width = (periodWidth * lengthPercentage) / 100;

    return <div className="tw-absolute tw-inset-0 tw-z-10 tw-h-0 tw-my-auto tw-border-t tw-border-t-charcoal tw-border-dashed" style={{ left, width }}></div>;
};

const AllocationInput = ({ id, monthKey, allocability }) => {
    const [field] = useField({ name: `${monthKey}.id_${id}`, type: 'number' });

    const isValid = useMemo(() => validateAllocationPercentage(field.value), [field.value]);

    return (
        <input
            {...field}
            value={field.value ?? ''}
            type="number"
            min={0}
            step={1}
            max={100}
            defaultValue={undefined}
            placeholder="%"
            className={classNames(
                'tw-w-full tw-h-full tw-text-sm tw-text-center tw-font-mono tw-appearance-none tw-border-2 tw-border-blue-500/70 focus:tw-outline-none focus:tw-ring-0 focus-visible:tw-outline-none focus-visible:tw-ring-0',
                {
                    'tw-border-red/75 focus:tw-border-red/75': !isValid,
                    'tw-border-amber-300 focus:tw-border-amber-300/75': isValid && allocability === 'has_allocation_outside_period',
                },
            )}
        />
    );
};

export default GanttChart;
